import { memo, ReactNode, useMemo, useRef } from 'react';

import {
  Box,
  Group,
  Modal as MantineModal,
  ModalProps as MantineModalProps,
  useMantineColorScheme
} from '@mantine/core';

import { useMergeClassNames } from '../../utils';

import { FooterClasses, ModalOverrideClassNames } from './Modal.styles';

interface ModalProps extends MantineModalProps {
  /**
   * Can be a ReactNode. Remember even a string is valid React Node.
   */
  header?: ReactNode;
  /**
   * Show/hide header bottom border
   */
  showHeaderUnderline?: boolean;
  /**
   * Show right aligned children
   */
  footerChildren?: ReactNode;
  /**
   * Show/hide border top for footer
   */
  footerBorder?: boolean;
  /**
   * remove default focus trap
   */
  defaultFocusTrap?: boolean;
  /**
   * remove default padding for content useful in cases where the content padding has to be controlled
   */
  noContentPadding?: boolean;
  /**
   * Show as bottom sheet
   */
  bottomSheet?: boolean;
}
/**
 * A modal component that works by displaying an overlay and taking the focus away.
 */
const Modal: React.FC<ModalProps> = memo(
  ({
    title,
    header,
    radius = 'xl',
    showHeaderUnderline = false,
    footerChildren,
    footerBorder,
    withCloseButton = false,
    noContentPadding = false,
    withinPortal = true,
    defaultFocusTrap = true,
    bottomSheet = false,
    ...props
  }) => {
    const { colorScheme } = useMantineColorScheme();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const classes = useMemo(() => {
      return ModalOverrideClassNames({
        showHeaderUnderline,
        noContentPadding,
        bottomSheet
      });
    }, [showHeaderUnderline, noContentPadding, bottomSheet]);

    const footerClasses = useMemo(() => {
      return FooterClasses({
        footerBorder
      });
    }, [footerBorder]);

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, props.classNames]
    });

    return (
      <MantineModal.Root
        {...props}
        classNames={mergedClassNames}
        radius={radius}
        withinPortal={withinPortal}
      >
        <MantineModal.Overlay
          opacity={colorScheme === 'dark' ? 0.7 : 0.42}
          {...props.overlayProps}
        />

        <MantineModal.Content p={0} radius={radius} ref={contentRef}>
          {title || header ? (
            <Group
              justify="space-between"
              pos={'sticky'}
              top={0}
              className={classes.header}
              aria-description="modal-header"
              component={'header'}
              wrap="nowrap"
              align="center"
            >
              {title ? (
                <MantineModal.Title>{title}</MantineModal.Title>
              ) : header ? (
                <>{header}</>
              ) : null}
              {withCloseButton && (
                <MantineModal.CloseButton size={20} {...props.closeButtonProps} />
              )}
            </Group>
          ) : null}

          {/* if header is present, header is rendered */}

          {/* {!header && title && (
            <MantineModal.Header>
              <MantineModal.Title>{title}</MantineModal.Title>
              {withCloseButton && (
                <MantineModal.CloseButton size={'xl'} {...props.closeButtonProps} />
              )}
            </MantineModal.Header>
          )} */}

          <MantineModal.Body>
            {defaultFocusTrap && <input hidden data-autofocus />}
            {props.children}
          </MantineModal.Body>
          {footerChildren && (
            <Box component="footer" className={footerClasses}>
              {footerChildren}
            </Box>
          )}
        </MantineModal.Content>
      </MantineModal.Root>
    );
  }
);

export { Modal };
export type { ModalProps };
